import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faArrowLeft,
  faLock,
  faEye,
  faEyeSlash,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import axios from '../config/axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';

const ResetPassword = () => {
  const { t } = useLanguage();
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`/auth/reset-password/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setMaskedEmail(response.data.email);
        }
      } catch (err) {
        setError(err.response?.data?.message || t('auth.invalidResetLink'));
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setValidating(false);
      setError(t('auth.noResetToken'));
    }
  }, [token]);

  // Check password strength
  useEffect(() => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password)
    });
  }, [password]);

  const isPasswordValid = () => {
    return Object.values(passwordStrength).every(Boolean) && password === confirmPassword;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid()) {
      setError(t('auth.meetPasswordRequirements'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`/auth/reset-password/${token}`, {
        password,
        confirmPassword
      });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="h-12 w-12 text-purple-500 animate-spin mb-4" />
          <p className="text-gray-400">{t('auth.validatingResetLink')}</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="mt-8 bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-700 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-900/50 mb-4">
              <FontAwesomeIcon icon={faTimesCircle} className="h-8 w-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">{t('auth.invalidResetLinkTitle')}</h3>
            <p className="text-gray-400 mb-6">
              {error || t('auth.resetLinkExpiredOrInvalid')}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth.resetLinksExpire')}
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-700 hover:to-purple-700"
            >
              {t('auth.requestNewLink')}
            </Link>
            <div className="mt-4">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-purple-400 hover:text-purple-300"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-red-600 to-purple-600 p-4 rounded-full">
              <FontAwesomeIcon icon={faShieldAlt} className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {t('auth.resetPasswordTitle')}
          </h2>
          {maskedEmail && (
            <p className="mt-2 text-center text-sm text-gray-400">
              {t('auth.settingNewPasswordFor', { email: maskedEmail })}
            </p>
          )}
        </div>

        <div className="mt-8 bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-700">
          {success ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-900/50 mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{t('auth.passwordResetSuccess')}</h3>
              <p className="text-gray-400 mb-6">
                {t('auth.passwordResetSuccessMessage')}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center text-purple-400 hover:text-purple-300"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {t('auth.goToLoginNow')}
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    {t('auth.newPassword')}
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-500" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-600 rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={t('auth.enterNewPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                {/* Password requirements */}
                <div className="bg-gray-700/50 rounded-md p-3 space-y-1">
                  <p className="text-xs text-gray-400 mb-2">{t('auth.passwordRequirements')}:</p>
                  <div className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-400' : 'text-gray-500'}`}>
                    <FontAwesomeIcon icon={passwordStrength.length ? faCheckCircle : faTimesCircle} className="mr-2" />
                    {t('auth.atLeast8Chars')}
                  </div>
                  <div className={`text-xs flex items-center ${passwordStrength.uppercase ? 'text-green-400' : 'text-gray-500'}`}>
                    <FontAwesomeIcon icon={passwordStrength.uppercase ? faCheckCircle : faTimesCircle} className="mr-2" />
                    {t('auth.oneUppercase')}
                  </div>
                  <div className={`text-xs flex items-center ${passwordStrength.lowercase ? 'text-green-400' : 'text-gray-500'}`}>
                    <FontAwesomeIcon icon={passwordStrength.lowercase ? faCheckCircle : faTimesCircle} className="mr-2" />
                    {t('auth.oneLowercase')}
                  </div>
                  <div className={`text-xs flex items-center ${passwordStrength.number ? 'text-green-400' : 'text-gray-500'}`}>
                    <FontAwesomeIcon icon={passwordStrength.number ? faCheckCircle : faTimesCircle} className="mr-2" />
                    {t('auth.oneNumber')}
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                    {t('auth.confirmPassword')}
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-500" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-600 rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={t('auth.confirmNewPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-400">{t('auth.passwordsNoMatch')}</p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="mt-1 text-xs text-green-400">{t('auth.passwordsMatch')}</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading || !isPasswordValid()}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('auth.resetting')}
                      </>
                    ) : (
                      t('auth.resetPassword')
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-purple-400 hover:text-purple-300"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
